import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';

const Overlay = styled(Box)({
  '&&': {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1300
  }
});

const Card = styled(Box)({
  '&&': {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    padding: 0,
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    // Custom scrollbar styling
    scrollbarWidth: 'thin',
    scrollbarColor: '#512EAB26 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
      background: 'transparent'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#512EAB26',
      borderRadius: 3,
      border: 'none'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#512EAB40'
    },
    '&::-webkit-scrollbar-button': {
      display: 'none'
    },
    '&::-webkit-scrollbar-corner': {
      background: 'transparent'
    }
  }
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  '&&': {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }
}));

const Content = styled(Box)({
  '&&': {
    minHeight: '100%'
  }
});

const PopupCard = ({
  open,
  onClose,
  children,
  width,
  height,
  maxWidth,
  maxHeight,
  className = '',
  showCloseButton = true,
  closeOnOverlayClick = true,
  ...props
}) => {
  if (!open) return null;

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  const cardStyle = {
    width,
    height,
    maxWidth: maxWidth || '90vw',
    maxHeight: maxHeight || '90vh'
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Card
        className={className}
        style={cardStyle}
        {...props}
      >
        {showCloseButton && (
          <CloseButton
            onClick={onClose}
            size="small"
            aria-label="close"
          >
            <CloseIcon fontSize="small" style={{ color: '#171717' }} />
          </CloseButton>
        )}
        <Content>
          {children}
        </Content>
      </Card>
    </Overlay>
  );
};

PopupCard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  showCloseButton: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool
};

PopupCard.defaultProps = {
  width: 'auto',
  height: 'auto',
  maxWidth: '90vw',
  maxHeight: '90vh',
  className: '',
  showCloseButton: true,
  closeOnOverlayClick: true
};

export default PopupCard;
